// frontend/src/components/EditTeacherForm.tsx

import React, { useState, useEffect, FormEvent } from "react";
import {
  X,
  UserRound,
  Mail,
  MapPin,
  BadgeDollarSign,
  CalendarDays,
  Hash,
  BookOpen,
  Check,
} from "lucide-react";
import { Teacher, UpdateTeacherRequest } from "../types/teacher";
import { getCourses } from "../api/courses";
import { Course } from "../types/course";

interface Props {
  teacher: Teacher;
  onSubmit: (id: string, data: UpdateTeacherRequest) => Promise<void>;
  onCancel: () => void;
}

// Utility to allow only number input
function handleNumberKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
  if (!/[0-9]/.test(e.key)) {
    e.preventDefault();
  }
}

export default function EditTeacherForm({
  teacher,
  onSubmit,
  onCancel,
}: Props) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [form, setForm] = useState<
    Omit<UpdateTeacherRequest, "salary" | "panNumber"> & {
      salary: string;
      panNumber: string;
    }
  >({
    name: teacher.name,
    email: teacher.email,
    address: teacher.address,
    salary: String(teacher.salary || ""),
    joinedDate: teacher.joinedDate.substr(0, 10),
    panNumber: teacher.panNumber || "",
    courses: teacher.courses.map((c) => c.id),
  });
  const [error, setError] = useState("");

  useEffect(() => {
    getCourses().then(setCourses);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "salary" || name === "panNumber") {
      setForm((prev) => ({
        ...prev,
        [name]: value.replace(/\D/g, ""),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const toggleCourse = (courseId: string) => {
    setForm((prev) => {
      const set = new Set(prev.courses || []);
      if (set.has(courseId)) set.delete(courseId);
      else set.add(courseId);
      return { ...prev, courses: Array.from(set) };
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await onSubmit(teacher.id, {
        ...form,
        salary: Number(form.salary || 0),
        panNumber: form.panNumber,
      } as UpdateTeacherRequest);
      onCancel();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const fieldClass = `
    box-border h-11 w-full min-w-0 rounded-[10px]
    border border-slate-200 bg-white px-3.5
    text-[13px] font-medium text-slate-800
    outline-none transition
    placeholder:text-slate-400
    hover:border-slate-300
    focus:border-emerald-400
    focus:ring-2 focus:ring-emerald-100
  `;

  const labelClass = `
    mb-1.5 block
    text-[10px] font-semibold uppercase
    tracking-[0.05em] text-slate-500
  `;

  const iconClass =
    "pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400";

  const selectedCourses = form.courses || [];

  return (
    <div
      className="
        fixed inset-0 z-[1600]
        flex items-center justify-center
        bg-slate-950/45 px-4 py-6
        backdrop-blur-[2px]
      "
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-teacher-form-title"
        className="
          flex max-h-[92vh] w-full max-w-[820px]
          flex-col overflow-hidden
          rounded-[18px]
          border border-slate-200
          bg-white
          shadow-[0_24px_70px_rgba(15,23,42,0.22)]
        "
      >
        <div
          className="
            flex shrink-0 items-start justify-between gap-4
            border-b border-slate-100
            px-6 py-5
            max-sm:px-4
          "
        >
          <div className="min-w-0">
            <p className="m-0 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">
              Teacher management
            </p>

            <h2
              id="edit-teacher-form-title"
              className="mb-0 mt-1 text-[20px] font-bold tracking-[-0.02em] text-slate-950"
            >
              Edit Teacher
            </h2>

            <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
              Update the teacher's details and assigned courses.
            </p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            aria-label="Close teacher form"
            title="Close"
            className="
              inline-flex h-9 w-9 shrink-0 items-center justify-center
              rounded-[9px]
              border border-emerald-600
              bg-emerald-600
              text-white transition
              hover:border-emerald-700
              hover:bg-emerald-700
              focus:outline-none
              focus:ring-2
              focus:ring-emerald-200
            "
          >
            <X size={17} />
          </button>
        </div>

        {error && (
          <div
            className="
              mx-6 mt-4 rounded-[10px]
              border border-red-200 bg-red-50
              px-4 py-3
              text-[12px] font-medium text-red-700
              max-sm:mx-4
            "
          >
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 px-6 py-5 sm:grid-cols-2 max-sm:px-4">
            <label className="min-w-0">
              <span className={labelClass}>Full Name</span>
              <div className="relative">
                <UserRound size={15} className={iconClass} />
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  placeholder="Enter full name"
                  onChange={handleChange}
                  required
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            <label className="min-w-0">
              <span className={labelClass}>Email Address</span>
              <div className="relative">
                <Mail size={15} className={iconClass} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  placeholder="Enter email"
                  onChange={handleChange}
                  required
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            <label className="min-w-0">
              <span className={labelClass}>Address</span>
              <div className="relative">
                <MapPin size={15} className={iconClass} />
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={form.address}
                  placeholder="Enter address"
                  onChange={handleChange}
                  required
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            <label className="min-w-0">
              <span className={labelClass}>PAN Number</span>
              <div className="relative">
                <Hash size={15} className={iconClass} />
                <input
                  id="panNumber"
                  name="panNumber"
                  type="text"
                  value={form.panNumber}
                  placeholder="123456789"
                  onChange={handleChange}
                  onKeyPress={handleNumberKeyPress}
                  inputMode="numeric"
                  maxLength={20}
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            <label className="min-w-0">
              <span className={labelClass}>Salary / Consultant Fees</span>
              <div className="relative">
                <BadgeDollarSign size={15} className={iconClass} />
                <input
                  id="salary"
                  name="salary"
                  type="text"
                  value={form.salary}
                  placeholder="500"
                  onChange={handleChange}
                  onKeyPress={handleNumberKeyPress}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  required
                  maxLength={12}
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            <label className="min-w-0">
              <span className={labelClass}>Joined Date</span>
              <div className="relative">
                <CalendarDays size={15} className={iconClass} />
                <input
                  id="joinedDate"
                  name="joinedDate"
                  type="date"
                  value={form.joinedDate}
                  onChange={handleChange}
                  required
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            <div className="min-w-0 sm:col-span-2">
              <div className="rounded-[14px] border border-slate-200 bg-slate-50/60 p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} className="text-slate-500" />
                      <h3 className="m-0 text-[14px] font-bold text-slate-900">
                        Available Courses
                      </h3>
                    </div>
                    <p className="mb-0 mt-1 text-[11px] text-slate-500">
                      Select the courses assigned to this teacher.
                    </p>
                  </div>

                  {selectedCourses.length > 0 && (
                    <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                      {selectedCourses.length} selected
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {courses.map((c) => {
                    const selected = selectedCourses.includes(c.id);

                    return (
                      <div
                        key={c.id}
                        tabIndex={0}
                        onClick={() => toggleCourse(c.id)}
                        onKeyPress={(e) => {
                          if (e.key === " " || e.key === "Enter")
                            toggleCourse(c.id);
                        }}
                        className={`
                          flex min-h-[48px] cursor-pointer
                          items-center justify-between gap-3
                          rounded-[10px] border
                          px-3.5 py-2.5
                          text-left transition
                          focus:outline-none
                          focus:ring-2 focus:ring-emerald-100
                          ${
                            selected
                              ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                          }
                        `}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0 rounded border-slate-300 accent-emerald-600"
                            checked={form.courses?.includes(c.id) || false}
                            onChange={() => toggleCourse(c.id)}
                            id={`course-${c.id}`}
                          />

                          <label
                            htmlFor={`course-${c.id}`}
                            className="min-w-0 cursor-pointer truncate text-[13px] font-semibold"
                          >
                            {c.name}
                          </label>
                        </div>

                        <span
                          className={`
                            grid h-5 w-5 shrink-0 place-items-center rounded-md border transition
                            ${
                              selected
                                ? "border-emerald-600 bg-emerald-600 text-white"
                                : "border-slate-300 bg-white text-transparent"
                            }
                          `}
                        >
                          <Check size={13} />
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/50 px-6 py-4 max-sm:px-4">
            <button
              type="button"
              onClick={onCancel}
              className="
                inline-flex h-10 items-center justify-center
                rounded-[9px]
                border border-emerald-200
                bg-white px-4
                text-[12px] font-semibold text-emerald-700
                transition
                hover:border-emerald-300
                hover:bg-emerald-50
                hover:text-emerald-800
                focus:outline-none
                focus:ring-2
                focus:ring-emerald-200
              "
            >
              Cancel
            </button>

            <button
              type="submit"
              className="
                inline-flex h-10 min-w-[126px]
                items-center justify-center
                rounded-[9px]
                border border-emerald-600
                bg-emerald-600 px-4
                text-[12px] font-semibold text-white
                transition
                hover:border-emerald-700
                hover:bg-emerald-700
                focus:outline-none
                focus:ring-2
                focus:ring-emerald-200
              "
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
